import { useCallback, useEffect, useRef, useState } from "react";

import AppShell from "../components/AppShell";
import {
  endAdminSupportSession,
  isOpen,
  listAdminSupportSessions,
  type AdminSupportSession,
  type SupportSessionStatus,
} from "../api/support";
import { useAuth } from "../stores/auth";

const POLL_MS = 10_000;

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function statusColor(s: SupportSessionStatus): string {
  switch (s) {
    case "active":
      return "bg-green-100 text-green-800";
    case "requested":
    case "awaiting_consent":
      return "bg-amber-100 text-amber-800";
    case "denied":
    case "expired":
      return "bg-red-100 text-red-800";
    default:
      return "bg-slate-100 text-slate-600";
  }
}

function statusLabel(s: SupportSessionStatus): string {
  switch (s) {
    case "awaiting_consent":
      return "awaiting consent";
    default:
      return s;
  }
}

export default function AdminSupport() {
  const tenant = useAuth((s) => s.tenant);
  const [sessions, setSessions] = useState<AdminSupportSession[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [revealedTicketId, setRevealedTicketId] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const rows = await listAdminSupportSessions();
      if (!mounted.current) return;
      setSessions(rows);
      setLoadError(null);
    } catch {
      if (mounted.current) setLoadError("Could not load support sessions.");
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  if (!tenant?.billing_exempt) {
    return (
      <AppShell>
        <div className="mx-auto max-w-lg py-16 text-center">
          <p className="text-slate-600">You don't have access to this page.</p>
        </div>
      </AppShell>
    );
  }

  async function onEnd(session: AdminSupportSession) {
    if (
      !window.confirm(
        `End the support session for ${session.target_agent_label || session.target_agent_hostname}? The customer's session UI will update on their next refresh.`,
      )
    ) {
      return;
    }
    setBusyId(session.id);
    setActionError(null);
    try {
      await endAdminSupportSession(session.id);
      await refresh();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      if (mounted.current) {
        setActionError(detail ?? "Couldn't end that session. Please try again.");
      }
    } finally {
      if (mounted.current) setBusyId(null);
    }
  }

  const openSessions = sessions?.filter((s) => isOpen(s.status)) ?? [];
  const recentSessions = sessions?.filter((s) => !isOpen(s.status)).slice(0, 50) ?? [];

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl">
        <h1 className="text-2xl font-semibold text-slate-900">Support Sessions</h1>
        <p className="mt-1 text-slate-600">
          Live and recent remote-help sessions across all customer offices.
          The page auto-refreshes every {Math.round(POLL_MS / 1000)} s.
        </p>

        {loadError && (
          <p role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {loadError}
          </p>
        )}
        {actionError && (
          <p role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {actionError}
          </p>
        )}

        <SessionTable
          title="Open"
          empty="No live sessions."
          rows={openSessions}
          busyId={busyId}
          revealedTicketId={revealedTicketId}
          onReveal={setRevealedTicketId}
          onEnd={onEnd}
          loading={sessions === null}
        />

        <SessionTable
          title="Recent (closed)"
          empty="No recent sessions."
          rows={recentSessions}
          busyId={busyId}
          revealedTicketId={revealedTicketId}
          onReveal={setRevealedTicketId}
          onEnd={onEnd}
          loading={sessions === null}
          terminal
        />
      </div>
    </AppShell>
  );
}

function SessionTable({
  title,
  empty,
  rows,
  busyId,
  revealedTicketId,
  onReveal,
  onEnd,
  loading,
  terminal = false,
}: {
  title: string;
  empty: string;
  rows: AdminSupportSession[];
  busyId: string | null;
  revealedTicketId: string | null;
  onReveal: (id: string | null) => void;
  onEnd: (s: AdminSupportSession) => void;
  loading: boolean;
  terminal?: boolean;
}) {
  return (
    <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-5">
      <h2 className="mb-3 text-base font-semibold text-slate-900">{title}</h2>
      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr>
                <th className="py-2 pr-3">Requested</th>
                <th className="py-2 pr-3">Office</th>
                <th className="py-2 pr-3">PC</th>
                <th className="py-2 pr-3">By</th>
                <th className="py-2 pr-3">Reason</th>
                <th className="py-2 pr-3">Status</th>
                {!terminal && <th className="py-2 pr-3">Connection</th>}
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((s) => {
                const pcLabel = s.target_agent_label || s.target_agent_hostname;
                const pcSub = s.target_agent_label ? s.target_agent_hostname : null;
                const revealed = revealedTicketId === s.id;
                return (
                  <tr key={s.id}>
                    <td className="py-2 pr-3 text-slate-600">{formatDate(s.requested_at)}</td>
                    <td className="py-2 pr-3 text-slate-900">{s.tenant_name}</td>
                    <td className="py-2 pr-3">
                      <span className="font-medium text-slate-900">{pcLabel}</span>
                      {pcSub && (
                        <span className="ml-2 font-mono text-xs text-slate-500">{pcSub}</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-slate-600">{s.requested_by_email ?? "—"}</td>
                    <td className="py-2 pr-3 text-slate-600">
                      {s.reason ? (
                        <span title={s.reason}>
                          {s.reason.length > 40 ? `${s.reason.slice(0, 40)}…` : s.reason}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${statusColor(
                          s.status,
                        )}`}
                      >
                        {statusLabel(s.status)}
                      </span>
                    </td>
                    {!terminal && (
                      <td className="py-2 pr-3 font-mono text-xs">
                        {s.rustdesk_ticket ? (
                          revealed ? (
                            <button
                              type="button"
                              onClick={() => onReveal(null)}
                              className="text-slate-900 underline hover:text-slate-700"
                              title="Hide"
                            >
                              {s.rustdesk_ticket}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => onReveal(s.id)}
                              className="text-slate-500 underline hover:text-slate-900"
                            >
                              Show ticket
                            </button>
                          )
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    )}
                    <td className="py-2 pr-3 text-right">
                      {!terminal && (
                        <button
                          type="button"
                          onClick={() => onEnd(s)}
                          disabled={busyId !== null}
                          className="no-tap-highlight rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                          {busyId === s.id ? "…" : "End"}
                        </button>
                      )}
                      {terminal && s.end_reason && (
                        <span className="text-xs text-slate-500">{s.end_reason.replace("_", " ")}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
